import { 
  Box, Move, RotateCw, Maximize2, MousePointer, Projector, Monitor, 
  Ruler, Camera, Thermometer, FileText, Settings, Share2, Grid3X3,
  Keyboard, Lightbulb, Eye, Copy, Trash2, Save, FolderOpen, Plus,
  Layers, LayoutGrid, ArrowUpDown, Pipette, ScanLine, Compass,
  FlipHorizontal, Cuboid, CircleDot, PanelLeft, BookOpen, Sparkles,
  Activity, Image as ImageIcon
} from 'lucide-react';

export const MANUAL_CATEGORIES = [
  {
    id: 'whats-new',
    title: "What's New (Feb 2026)",
    icon: Sparkles,
    color: '#f0abfc',
    description: 'Recent feature additions and UX improvements',
    sections: [
      {
        title: 'Project Type Selector',
        content: `When you create a new project (**File ▸ New Project**), you now choose a **Project Type**:

**Projection** — Sets up the editor for projector-based mapping. After creation, the **Library Panel auto-opens the Projectors tab**.

**LED** — Sets up the editor for LED wall builds. After creation, the **Library Panel auto-opens the LED tab**.

The selection is purely an opener-shortcut — the full library remains available regardless of project type.`,
        tips: ['You can still switch tabs at any time after creation.', 'Combine with the Measurement Units picker to start each project pre-configured for your workflow.']
      },
      {
        title: 'Display Mode (Outline / Shaded)',
        content: `New toolbar toggle (next to the Lux Heatmap thermometer) lets you switch the entire scene between two render modes:

**Shaded** (default) — Full materials, textures, lighting, shadows.

**Outline** — Wireframe rendering. Lighter on the GPU and ideal for:
- Object alignment
- Layout planning
- Technical positioning checks

Use the **FPS meter** in the top-right to verify the perf gain — fragment-bound scenes (heavy projection) typically see a noticeable boost.

**Important:** Outline is visual-only — projection math, LED logic, truss/scaffolding calculations, gizmos, and shadows all keep running normally.`,
        tips: ['Great as a "design view" when you want to see structure rather than visuals.', 'Toggling in/out is instant — try both and watch the FPS counter to gauge the gain on your specific scene.']
      },
      {
        title: 'Real-time FPS Meter',
        content: `A live performance badge in the top-right toolbar shows the engine's current frame rate, color-coded:

**🟢 60+ fps** — Smooth
**🟡 30-59 fps** — Medium
**🔴 < 30 fps** — Heavy / lag

The badge polls the Babylon engine every 500 ms — it's read-only and never affects scene state.`,
        tips: ['Watch the meter while toggling Outline mode or hiding heavy LED walls to see exactly which assets cost the most frames.']
      },
      {
        title: 'Library Panel — 3-column Tab Layout',
        content: `The Library tabs were reorganized for readability:

- **Row 1**: Projectors · Truss & Scaffold · LED
- **Row 2**: Objects · Files · Content
- **Row 3**: LED Test Pattern (full width)

Each tab now uses a **fuchsia accent + glow** when active, so the current section is instantly identifiable. The LED tab also uses a **2-column grid** of LED variants (Screen / Curved / Corner / Soft Corner / Flexible).`,
        tips: ['The "Truss & Scaffold" tab merges what used to be separate menus.', 'The active fuchsia accent matches the rest of the app for visual consistency.']
      },
      {
        title: 'Hide / Lock — Behaviour Fixes',
        content: `Both Scene-panel toggles now work **instantly** for projectors and objects:

**Hide** — Actually removes the projector body, lens, frame, beam wireframe, spotlight, and on-screen label from the viewport (state and projection math preserved).

**Lock** — Detaches the gizmo and yellow selection box on the next frame (no need to switch tools to "refresh" anymore). Unlocking re-attaches the gizmo if a transform tool is still active.

Visual cleanup: the duplicate yellow lock indicator that used to appear next to the toggle button is gone — only one lock icon now per row.`,
        tips: ['Use Lock to freeze a finalized rig section before making finer edits to nearby objects.', 'Hide is reversible — Show restores everything exactly as it was.']
      },
      {
        title: 'Undo / Redo — Robustness Overhaul',
        content: `The undo/redo system was rewritten to fix the "objects disappear" bug. Every change is now correctly tracked, including:

- Object move / rotate / scale (gizmo or input field)
- Add / delete / duplicate object
- Show / hide toggle
- Lock / unlock toggle
- LED tile, curve, corner, pixel-pitch changes
- Projector position, lens, throw ratio, lumens, etc.

**How it works (under the hood):** A snapshot of the *before-change* state is captured at the FIRST call of an action batch and held intact through a 250 ms debounce window. Subsequent calls only extend the timer — they never overwrite the captured snapshot. Result: every Ctrl+Z always restores the state exactly as it was before you started editing.

**Shortcuts:** **Ctrl+Z** undo · **Ctrl+Shift+Z** or **Ctrl+Y** redo.`,
        tips: ['Rapid edits (drags, repeated input changes) collapse into a single undo step — no more spamming Ctrl+Z.', 'Add/Delete are immediate undo entries — no debounce.']
      },
      {
        title: 'Viewport Controls (Top-right)',
        content: `Three viewport-overlay buttons in the top-right of the 3D area, all wired up:

**🔲 Toggle Grid** — Show/hide the ground reference grid. Highlights fuchsia when on.

**📷 Take Screenshot** — Downloads a clean PNG of the current viewport (no UI overlays). Uses Babylon's native render-target capture for full resolution.

**⛶ Fullscreen** — Expands the viewport to full screen via the browser Fullscreen API. Press Esc or click again to exit. Icon turns fuchsia when active.`,
        tips: ['Screenshots capture only the 3D canvas — perfect for client decks.', 'Fullscreen pairs nicely with Outline mode for distraction-free layout reviews.']
      },
      {
        title: 'Projector POV Window',
        content: `Each projector can spawn a **floating preview window** showing the projector's exact point-of-view (what it actually projects).

**Features:**
- **Aspect-locked** to the projector's native ratio
- **Native fullscreen** support
- **Multi-monitor pop-out** via \`window.open\` — drag the popped window to a second monitor for live "stage" preview
- 30 fps cap, 720p resolution limit, auto-pause when the tab is hidden

To open: select a projector → Properties panel → POV button (camera icon).`,
        tips: ['Use multi-monitor pop-out during real shows: secondary monitor mirrors what the physical projector sees.', 'POVs render via Babylon RenderTargetTexture — they share the main scene state for full accuracy.']
      },
      {
        title: 'Branded PDF Reports',
        content: `All three PDF exports — **Projection Report**, **Truss Report**, **Scaffolding Report** — now embed the AV Simulation Tool logo in the cover-page header.

The PDF logo is a 512px optimized version (114 KB) so embedded reports stay lean. Existing report content (specs tables, lux ratings, weight breakdowns, structural calcs) is untouched.`,
        tips: ['Use the report PDFs as client deliverables — branding is now consistent with the editor and auth pages.']
      },
      {
        title: 'Toast Notifications — Bottom-center',
        content: `Status messages (saved, undone, projector added, etc.) now appear at the **bottom-center of the 3D viewport** rather than the bottom-left, so they no longer obscure the Library Panel during edits.`,
        tips: ['Toasts auto-dismiss — hover to extend.']
      }
    ]
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    color: '#a78bfa',
    description: 'Interface overview, panels, and basic navigation',
    sections: [
      {
        title: 'Interface Overview',
        content: `**AV Simulation Tool** is a professional 3D projection mapping & LED design platform. The interface consists of:

**Logo & Toolbar** (Top) — AV Simulation Tool branding (top-left), then Save/Open/New, Undo/Redo, Transform tools (Select/Move/Rotate/Scale), Snap, Lux Heatmap, **Display Mode (Shaded/Outline)** toggle, FPS meter, and Cloud-Connected status.

**Menu Bar** — File, Edit, Select, Objects, Truss, Projectors, LED, Viewport, Measure, Help.

**Library Panel** (Left) — 3-row tab grid: **Projectors** · **Truss & Scaffold** · **LED** · Objects · Files · Content · **LED Test Pattern**. Active tab glows fuchsia.

**3D Viewport** (Center) — Main workspace. Top-right floating controls: **Grid toggle**, **Screenshot**, **Fullscreen**.

**Scene Panel** (Right top) — Hierarchical list of all objects with per-row Hide / Lock / POV / Delete actions.

**Properties Panel** (Right bottom) — Edit the selected object (transform, dimensions, projector lens, LED config, etc.).

**Camera Tab** — Saved views, FOV, screenshot export.

**Settings Tab** — Project name, indoor/outdoor, units.`,
        tips: ['Use the left panel tabs to switch between gear categories.', 'Right-click + drag to orbit, scroll to zoom.']
      },
      {
        title: 'Creating Your First Project',
        content: `**Step 1:** Click **File ▸ New Project** or press **Ctrl+N**.

**Step 2:** Pick a **Project Type** (Projection or LED) — the matching Library tab opens automatically.

**Step 3:** Set the **Measurement Units** (mm, cm, m, ft, in).

**Step 4:** Add surfaces (walls, floors, screens) from **Objects** menu.

**Step 5:** Add projectors from the **Projectors** menu / Library panel, or LED panels from **LED**.

**Step 6:** Position and aim equipment using the gizmos (Move/Rotate/Scale or input fields).

**Step 7:** Toggle the **Lux Heatmap** to verify coverage. Use **Outline** mode for layout cross-checks.

**Step 8:** **Ctrl+S** saves to cloud.`,
        tips: ['Start with surfaces before adding projectors — they need targets.', 'Watch the FPS meter to keep your scene healthy as it grows.']
      },
      {
        title: 'Cloud Save & Auto-Save',
        content: `Projects autosave to the cloud. The status indicator (top-right toolbar) shows:

**🟢 Cloud Connected** — Synced and saved.

You can also:
- **Quick Save (Local)** — Browser backup.
- **Load Saved** — Restore the local backup.
- **Save As** — Copy with a new name.

The asterisk (*) next to the project name indicates unsaved changes.`,
        tips: ['Cloud Connected = safe to close the tab.', 'Use Save As to keep multiple variants for client review.']
      }
    ]
  },
  {
    id: 'objects',
    title: 'Objects & Primitives',
    icon: Box,
    color: '#60a5fa',
    description: 'Add and configure 3D shapes, walls, screens, and more',
    sections: [
      {
        title: 'Basic Primitives',
        content: `Add shapes from **Objects** menu:

**Cube** — Basic box shape. Resize to create walls, pillars, or any rectangular structure.

**Sphere** — Spherical object for domes or decorative elements.

**Wall/Plane** — Flat surface ideal for projection mapping targets.

**Cylinder** — Round column shape.

**Ground** — Large flat plane for the floor/ground surface.

**Dome** — Half-sphere shape for immersive projection environments.

**Pipe** — Tubular shape for rigging or structural elements.

**Scrim Screen** — Transparent/semi-transparent projection surface.`,
        tips: ['Planes/Walls are the most common projection surfaces.', 'Use the Properties panel to precisely set dimensions after adding.']
      },
      {
        title: 'Projection Screens',
        content: `Pre-configured screens are available under **Objects > Projection Screen**:

- **16:10 Small** (2m x 1.25m)
- **16:10 Medium** (4m x 2.5m)  
- **16:10 Large** (6m x 3.75m)

These come pre-sized with correct aspect ratios for standard projection.`,
        tips: ['Projection screens are optimized as projection targets with proper aspect ratios.']
      },
      {
        title: 'Object Properties',
        content: `Select any object to edit in the Properties panel:

**Transform** — Position (X, Y, Z), Rotation, Scale. Values shown in your selected unit (mm, cm, m, ft, in).

**Dimensions** — Width, Height, Depth of the object.

**Name** — Rename objects for better organization in the Scene panel.

**Visibility** — Toggle object visibility with the eye icon in the Scene panel.

**Color** — Change the object's base color.

**Resolution** — For projection surfaces, shows calculated pixel density per face (width x height pixels at 300 px/m).`,
        tips: ['Double-click an object name in the Scene panel to rename it.', 'Use the lock icon to prevent accidental modifications.']
      },
      {
        title: 'Mirror Objects',
        content: `Mirror objects create real-time reflections in your scene. They simulate reflective surfaces like mirrors, glossy floors, or polished walls.

Mirrors interact with projector beams and reflect light realistically, allowing you to plan projection setups that utilize reflective surfaces.`,
        tips: ['Mirrors can significantly impact scene rendering performance with many projectors.']
      }
    ]
  },
  {
    id: 'projectors',
    title: 'Projectors',
    icon: Projector,
    color: '#f472b6',
    description: 'Projector library, lenses, beam simulation, and configuration',
    sections: [
      {
        title: 'Projector Library',
        content: `The Library panel (left sidebar, "Projectors" tab) contains professional projector models from:

**Barco** — UDX 4K40 Flex, UDX 4K32, G100 W22, and more.
**Panasonic** — Various models with complete specifications.

Each projector card shows:
- Lumens rating
- Aspect ratio
- Throw ratio range
- Physical size and weight
- Available lens options

Click **"+ Add to Scene"** to place a projector, or **"Manage Lenses"** to view/add lenses.`,
        tips: ['You can add custom lenses to any projector model.', 'The Library shows all compatible lens options for each projector.']
      },
      {
        title: 'Projector Properties',
        content: `Select a projector to see its properties:

**Position & Rotation** — Precise placement in 3D space.

**Throw Ratio** — Controls the projection cone width. Lower = wider image. Adjustable via slider within the lens range.

**Throw Distance** — Distance from projector to the target surface.

**Lumens** — Brightness output of the projector.

**Aspect Ratio** — 16:10, 16:9, 4:3, etc.

**Power On/Off** — Toggle the projector's beam.

**Show Name** — Display the projector name label in the viewport.

**Beam Visibility** — Show/hide the projection cone wireframe.`,
        tips: ['Adjust throw ratio to match your desired image size at the given distance.', 'Use "Show All Beams" / "Hide All Beams" from the Projectors menu to toggle all at once.']
      },
      {
        title: 'Lens System',
        content: `Each projector supports multiple lens configurations:

**Standard Lens** — Default straight projection.

**90-Degree Lens (Lens90)** — Projects at a right angle from the projector body. Has 4 rotation options:
- **Top** — Projects upward
- **Bottom** — Projects downward
- **Left** — Projects to the left
- **Right** — Projects to the right

**Lens Shift** — Horizontal and vertical offset (percentage) to adjust the projection without moving the projector body.

**Custom Lenses** — Add your own lens specs with min/max throw ratio, lens shift values, and weight.`,
        tips: ['Lens90 is essential for tight spaces where projectors cannot face the surface directly.', 'Lens shift allows fine-tuning projection alignment without physically moving the projector.']
      },
      {
        title: 'Reverse Projectors',
        content: `Reverse projectors project from behind a translucent surface (rear projection). When a projector is marked as "Reverse," the beam direction is inverted.

**Key Properties:**
- Indicated by an amber "Reverse Projector" badge in the Properties panel.
- Projection calculations account for the reversed beam path.
- Useful for rear-projection screens, shop windows, and holographic effects.`,
        tips: ['Reverse projectors are commonly used with scrim screens and translucent materials.']
      },
      {
        title: 'Projection Report',
        content: `Generate a comprehensive PDF report from **Projectors > Export Projection Report** or **File > Export PDF**.

The report includes:
- Project summary (name, type, projector count)
- Per-surface projection analysis
- Projector specifications (model, lumens, throw ratio, lens)
- Lux levels with color-coded ratings
- Weight breakdown (body + lens + frame + clamp)
- Overlap analysis between projectors

The report uses a professional grid layout organized by projection surface.`,
        tips: ['The report is a critical deliverable for client presentations and technical planning.', 'Indoor and Outdoor modes use different lux rating scales.']
      }
    ]
  },
  {
    id: 'led-screens',
    title: 'LED Screens',
    icon: Monitor,
    color: '#34d399',
    description: 'LED walls, curved displays, flexible panels, and content management',
    sections: [
      {
        title: 'LED Screen Types',
        content: `Four types of LED screens are available from the **LED** menu:

**Standard LED Wall** — Flat LED panel grid. Configure tile size, pixel pitch, rows, and columns.

**Curved LED Wall** — LED panels arranged in a curve. Set the curve angle to control the bend.

**Flexible LED Wall** — Highly bendable LED panels for organic shapes and wraps.

**Corner LED Tile** — Specialized tiles for inside/outside corners.`,
        tips: ['Use the LED tab in the Library panel for quick access to LED test patterns.']
      },
      {
        title: 'LED Properties',
        content: `LED screen properties include:

**Tile Configuration** — Width, height, depth of individual tiles in mm.

**Grid Layout** — Number of columns and rows of tiles.

**Pixel Pitch** — Distance between LED pixels (mm). Smaller = higher resolution.

**Power** — Per-tile and maximum power consumption in watts.

**Curve Settings** — For curved/flexible types: curve angle, smooth bend toggle.

**Content** — Assign images or test patterns to the LED surface via the Content Browser.`,
        tips: ['Pixel pitch directly affects the minimum viewing distance for the audience.', 'Total power consumption is shown to help plan electrical requirements.']
      },
      {
        title: 'LED Test Patterns',
        content: `The LED Test tab in the Library panel provides standard test patterns:

These patterns help verify:
- Pixel alignment and dead pixel detection
- Color accuracy and uniformity
- Brightness calibration
- Content scaling and aspect ratio

Apply test patterns by selecting an LED screen and choosing from the Content Browser.`,
        tips: ['Test patterns are essential during physical setup to verify LED wall quality.']
      }
    ]
  },
  {
    id: 'truss',
    title: 'Truss System',
    icon: Cuboid,
    color: '#fb923c',
    description: 'Truss sections, corner pieces, and structural configuration',
    sections: [
      {
        title: 'Adding Truss',
        content: `Add truss from the **Truss** menu or Library panel:

- **2m Section** — Standard 2-meter truss length
- **3m Section** — Standard 3-meter truss length
- **4m Section** — Standard 4-meter truss length
- **Corner Piece** — 0.5m corner connector

Truss sections are modeled with realistic cross-member geometry.`,
        tips: ['Build complete truss rigs by combining sections and corner pieces.']
      },
      {
        title: 'Truss Properties',
        content: `Select a truss to configure:

**Length** — Adjustable truss section length.

**Cross Section** — Profile size and type.

**Weight** — Calculated based on section length and type.

**Load Capacity** — Maximum weight the truss can support.

**Connection Points** — Where other truss sections or equipment can attach.

The properties panel provides a detailed breakdown of truss specifications for rigging calculations.`,
        tips: ['Always verify load calculations before physical installation.', 'Corner pieces allow building rectangular and L-shaped truss configurations.']
      }
    ]
  },
  {
    id: 'scaffolding',
    title: 'Scaffolding',
    icon: Layers,
    color: '#fbbf24',
    description: 'Scaffolding towers, decks, stairs, and load calculations',
    sections: [
      {
        title: 'Scaffolding System',
        content: `Scaffolding objects provide realistic tower structures for projector mounting and equipment platforms.

**Configuration includes:**
- Bay width, length, and lift height
- Number of levels/lifts
- Deck placement per level (multiple decks per level)
- Stair configuration per level
- Guard rail options

The scaffolding system calculates load limits and displays warnings when weight capacity is approached.`,
        tips: ['Add decks to specific levels using the deck editor in the Properties panel.', 'Stair configuration allows planning safe crew access to elevated positions.']
      },
      {
        title: 'Load Calculations',
        content: `The scaffolding system includes built-in load calculations:

- **Per-level weight** — Equipment and personnel on each deck
- **Total structure weight** — Combined weight of all levels
- **Capacity warnings** — Visual alerts when approaching limits

Future enhancements will include bracing toggles (1.5x multiplier) and foundation capacity checks.`,
        tips: ['Monitor load indicators in the Properties panel when adding heavy equipment to scaffolding levels.']
      }
    ]
  },
  {
    id: 'camera',
    title: 'Camera System',
    icon: Camera,
    color: '#a78bfa',
    description: 'Saved views, FOV control, view presets, and screenshot export',
    sections: [
      {
        title: 'Viewport Navigation',
        content: `Control the 3D camera:

**Orbit** — Right-click + drag to orbit around the scene.

**Pan** — Middle-click + drag (or Shift + right-click) to pan the view.

**Zoom** — Scroll wheel to zoom in/out.

**View Presets** — Use the **Viewport** menu for preset views:
- Top / Bottom / Left / Right / Front / Back views
- Reset Camera (return to default position)`,
        tips: ['Use view presets to quickly switch to orthographic-style views for precise alignment.']
      },
      {
        title: 'Saved Camera Views',
        content: `The Camera tab in the Properties panel allows saving and managing camera positions:

**Save Current View** — Captures your current camera position, target, and FOV as a named view.

**Switch Views** — Click any saved view to smoothly animate the camera to that position.

**FOV Control** — Adjust field of view (default 45-degrees) with the FOV slider.

**Export Screenshot** — Capture a PNG image of any saved view.

**Delete Views** — Remove saved views you no longer need.

Camera views are saved to the cloud with your project.`,
        tips: ['Save key presentation angles as named views for quick switching during client reviews.', 'FOV affects the perceived perspective distortion — lower values for flatter, technical views.']
      }
    ]
  },
  {
    id: 'lux-heatmap',
    title: 'Lux & Heatmap',
    icon: Thermometer,
    color: '#ef4444',
    description: 'Lux calculations, brightness ratings, and visual heatmap overlay',
    sections: [
      {
        title: 'Lux Calculation',
        content: `Lux is calculated for each projection surface using the formula:

**Lux = (Lumens x 0.70) / Area (m-sq)**

Where:
- **Lumens** — Projector brightness output
- **0.70** — Default optical efficiency factor
- **Area** — Projected image area in square meters

The lux value is shown in the Properties panel when a projector is selected, along with a color-coded rating.`,
        tips: ['Higher lux = brighter projection. Indoor events typically need 300-600 lux, outdoor may need 1000+.']
      },
      {
        title: 'Lux Ratings',
        content: `Lux levels are color-coded by rating:

**Indoor Ratings:**
- Excellent (Green) — 500+ lux
- Good (Yellow-Green) — 300-500 lux
- Acceptable (Yellow) — 150-300 lux
- Low (Orange) — 75-150 lux
- Very Low (Red) — Below 75 lux

**Outdoor Ratings:** (Higher thresholds)
- Excellent — 2000+ lux
- Good — 1000-2000 lux
- And so on...

Set Indoor/Outdoor in **Settings** tab to use the appropriate rating scale.`,
        tips: ['Switch between Indoor and Outdoor mode in Settings to get accurate lux ratings for your environment.']
      },
      {
        title: 'Heatmap Visualization',
        content: `Toggle the Lux Heatmap from the **toolbar thermometer icon** or via the Heatmap button.

**When enabled:**
- Colored overlay planes appear on all projection surfaces
- Color gradient: Blue (low) to Green (medium) to Red (high lux)
- Numeric lux labels are displayed on each surface area
- A legend appears in the top-left showing the gradient scale (0-600 lux)
- Combined lux from overlapping projectors is shown (additive)

**When disabled:**
- All heatmap overlays and legend are cleanly removed
- Scene returns to normal projection view

The heatmap accounts for:
- Projector position, rotation, and lens type (including Lens90)
- Lens shift offsets
- Throw ratio and beam spread
- Multiple projector overlap (additive lux)`,
        tips: ['Use the heatmap to identify dark spots and over-lit areas in your projection design.', 'Red areas indicate high lux (good for bright environments), blue areas may need additional coverage.']
      }
    ]
  },
  {
    id: 'measurements',
    title: 'Measurements',
    icon: Ruler,
    color: '#14b8a6',
    description: 'Point-to-point distance measurement tools',
    sections: [
      {
        title: 'Distance Measurement',
        content: `Access via **Measure** menu or press **M**:

**Point-to-Point Distance:**
1. Enable measurement mode (Measure > Point-to-Point Distance or press M)
2. Click the first point in the 3D viewport
3. Click the second point
4. The distance is displayed in your selected unit

Measurements persist in the scene until cleared.`,
        tips: ['Use measurements to verify throw distances between projectors and surfaces.', 'Press M again to toggle measurement mode off.']
      },
      {
        title: 'Managing Measurements',
        content: `**Clear All** — Use **Measure > Clear All Measurements** to remove all distance markers.

Measurements are saved with your project and display in the selected unit system (mm, cm, m, ft, in).`,
        tips: ['Measurements are useful for verifying projector placement against technical specifications.']
      }
    ]
  },
  {
    id: 'transform-tools',
    title: 'Selection & Transform',
    icon: Move,
    color: '#818cf8',
    description: 'Select, move, rotate, scale, snap, duplicate, and delete objects',
    sections: [
      {
        title: 'Selection',
        content: `**Click** — Select an object in the 3D viewport or Scene panel.

**Ctrl+Click** — Multi-select multiple objects.

**Ctrl+A** — Select all objects.

**Escape** — Deselect all.

**Scene Panel** — Click object names to select. Use visibility (eye) and delete icons for quick management.`,
        tips: ['Multi-selected objects can be duplicated or deleted as a group.']
      },
      {
        title: 'Transform Tools',
        content: `Switch tools via the toolbar or keyboard shortcuts:

**Select Tool** (V) — Click to select objects without transforming.

**Move Tool** (W) — Drag the XYZ gizmo arrows to move objects along axes.

**Rotate Tool** (E) — Drag the rotation rings to rotate objects around axes.

**Scale Tool** (R) — Drag the scale handles to resize objects.

**Pan Tool** — Pan the viewport camera.

Each tool shows a colored gizmo:
- **Red** = X axis
- **Green** = Y axis
- **Blue** = Z axis`,
        tips: ['Hold Shift while dragging for constrained movement along a single axis.', 'Use the Properties panel for precise numeric input instead of dragging.']
      },
      {
        title: 'Snapping',
        content: `Toggle snapping with the **magnet icon** in the toolbar.

When enabled, objects snap to grid increments while moving, rotating, or scaling. This helps with precise alignment and consistent spacing.`,
        tips: ['Snapping is especially useful when aligning multiple projectors or building truss rigs.']
      },
      {
        title: 'Duplicate & Delete',
        content: `**Duplicate** (Ctrl+D) — Creates a copy of the selected object at the same position.

**Duplicate Multi-Select** — With multiple objects selected, Ctrl+D duplicates all of them.

**Delete** (Delete/Backspace) — Removes the selected object from the scene.

**Multi-Delete** — With multiple objects selected, Delete removes all of them.`,
        tips: ['Duplicate is faster than adding a new object from the menu when you need similar configurations.']
      }
    ]
  },
  {
    id: 'viewport',
    title: 'Viewport & Display',
    icon: Eye,
    color: '#06b6d4',
    description: 'Grid, view presets, perspective controls, and display options',
    sections: [
      {
        title: 'View Presets',
        content: `Access from the **Viewport** menu:

- **Reset Camera** — Return to the default 3D perspective view
- **Top View** — Looking straight down (plan view)
- **Bottom View** — Looking straight up
- **Left/Right View** — Side elevations
- **Front/Back View** — Front and rear elevations

The current view mode is shown in the top-center of the viewport.`,
        tips: ['Use Top View for layout planning and Front/Side views for height verification.']
      },
      {
        title: 'Grid & Display',
        content: `**Toggle Grid** (G) — Show/hide the ground grid for spatial reference.

The grid helps with:
- Object alignment
- Distance estimation
- Scene orientation

Grid visibility is also available from **Viewport > Toggle Grid** and **Viewport > Show/Hide Grid**.`,
        tips: ['The grid scale adjusts automatically based on your zoom level.']
      }
    ]
  },
  {
    id: 'export-share',
    title: 'Export & Sharing',
    icon: Share2,
    color: '#f97316',
    description: 'PDF reports, image export, and shareable view-only links',
    sections: [
      {
        title: 'Export Options',
        content: `**Export PDF** (File > Export PDF) — Generates a projection report with all projector specs, lux analysis, and surface data.

**Export Image** (File > Export Image) — Captures a screenshot of the current viewport.

**Export Projection Report** (Projectors menu) — Detailed multi-page PDF with per-surface analysis, projector specifications, lux ratings, and weight breakdown.`,
        tips: ['Use Projection Report for client deliverables and technical documentation.']
      },
      {
        title: 'Shareable Links',
        content: `Generate a view-only shareable link from **File > Get Shareable Link**.

The shared link allows anyone to:
- View the complete 3D scene
- Orbit, pan, and zoom the camera
- See all objects and projector placements

**Note:** Shared links are read-only — viewers cannot modify the project.`,
        tips: ['Share links with clients for review before final installation.', 'Each project has a unique share token.']
      }
    ]
  },
  {
    id: 'content-browser',
    title: 'Content Browser',
    icon: LayoutGrid,
    color: '#ec4899',
    description: 'Manage images, textures, and media content for LED screens',
    sections: [
      {
        title: 'Content Management',
        content: `The Content tab in the Library panel allows managing media content:

- **Upload images** for LED screen content
- **Organize** content in folders
- **Apply** content to LED screens via the Content Browser

When an LED screen is selected, the Content Browser mode changes to show assignable content.`,
        tips: ['Prepare content at the correct resolution for your LED pixel pitch to ensure sharp display.']
      }
    ]
  },
  {
    id: 'settings',
    title: 'Project Settings',
    icon: Settings,
    color: '#94a3b8',
    description: 'Project name, indoor/outdoor type, units, and world settings',
    sections: [
      {
        title: 'Project Configuration',
        content: `Access from the **Settings** tab in the right panel:

**Project Name** — Set a descriptive name for your project.

**Project Type** — Choose **Indoor** or **Outdoor**:
- Affects lux rating thresholds (outdoor needs much higher lux)
- Influences the Projection Report ratings

**Measurement Units** — Choose your preferred unit system:
- Millimeters (mm)
- Centimeters (cm)
- Meters (m)
- Feet (ft)
- Inches (in)

All position, dimension, and distance values update to reflect the selected unit.`,
        tips: ['Set the correct Indoor/Outdoor type before reviewing lux ratings — the scales are very different.']
      },
      {
        title: 'World Settings',
        content: `World settings control the global scene environment:

**Grid Visibility** — Show/hide the reference grid.

These settings are saved with your project.`,
        tips: ['World settings persist across sessions when saved to the cloud.']
      }
    ]
  },
  {
    id: '3d-files',
    title: '3D Model Import',
    icon: Cuboid,
    color: '#8b5cf6',
    description: 'Import custom 3D models (OBJ files) into your scene',
    sections: [
      {
        title: 'Importing Models',
        content: `Import custom 3D models using the **import button** in the toolbar or the **Files** tab in the Library panel.

**Supported Formats:**
- OBJ files (.obj)

Imported models can be positioned, rotated, and scaled like any other object. They appear in the Scene panel and can be used as projection surfaces.`,
        tips: ['Import venue/stage models for accurate projection planning against real-world geometry.', 'Large models may take a moment to load — check the progress indicator.']
      }
    ]
  }
];

export const KEYBOARD_SHORTCUTS = [
  {
    category: 'File Operations',
    shortcuts: [
      { keys: 'Ctrl + S', action: 'Save project to cloud' },
      { keys: 'Ctrl + Shift + S', action: 'Save As (create copy)' },
      { keys: 'Ctrl + N', action: 'New project (with Project Type + Units picker)' },
      { keys: 'Ctrl + O', action: 'Open project' },
    ]
  },
  {
    category: 'Edit / Undo / Redo',
    shortcuts: [
      { keys: 'Ctrl + Z', action: 'Undo (snapshot-based — restores exact pre-change state)' },
      { keys: 'Ctrl + Shift + Z', action: 'Redo' },
      { keys: 'Ctrl + Y', action: 'Redo (alternative)' },
      { keys: 'Ctrl + D', action: 'Duplicate selected (works with multi-select)' },
      { keys: 'Delete / Backspace', action: 'Delete selected (works with multi-select)' },
    ]
  },
  {
    category: 'Transform Tools',
    shortcuts: [
      { keys: 'V', action: 'Select tool' },
      { keys: 'W', action: 'Move tool' },
      { keys: 'E', action: 'Rotate tool' },
      { keys: 'R', action: 'Scale tool' },
      { keys: 'Shift (hold)', action: 'Precision mode for gizmo movement' },
    ]
  },
  {
    category: 'Selection',
    shortcuts: [
      { keys: 'Ctrl + A', action: 'Select all objects' },
      { keys: 'Ctrl + Click', action: 'Multi-select objects' },
      { keys: 'Escape', action: 'Deselect all' },
    ]
  },
  {
    category: 'Viewport & Navigation',
    shortcuts: [
      { keys: '0', action: 'Perspective (Reset) view' },
      { keys: '1', action: 'Front orthographic view' },
      { keys: 'Ctrl + 1', action: 'Back orthographic view' },
      { keys: '3', action: 'Right orthographic view' },
      { keys: 'Ctrl + 3', action: 'Left orthographic view' },
      { keys: '7', action: 'Top orthographic view' },
      { keys: 'Ctrl + 7', action: 'Bottom orthographic view' },
      { keys: 'G', action: 'Toggle ground grid visibility' },
      { keys: 'Right-click + Drag', action: 'Orbit camera' },
      { keys: 'Middle-click + Drag', action: 'Pan camera' },
      { keys: 'Scroll Wheel', action: 'Zoom in/out' },
    ]
  },
  {
    category: 'Projector Controls',
    shortcuts: [
      { keys: 'B', action: 'Show all projector beams' },
      { keys: 'Shift + B', action: 'Hide all projector beams' },
      { keys: 'Shift + P', action: 'Toggle all projectors ON/OFF' },
      { keys: 'Shift + O', action: 'Toggle all projector borders ON/OFF' },
    ]
  },
  {
    category: 'Camera Views',
    shortcuts: [
      { keys: 'Ctrl + Shift + C', action: 'Save current camera view' },
    ]
  },
  {
    category: 'Measurement',
    shortcuts: [
      { keys: 'M', action: 'Toggle measurement mode (point-to-point distance)' },
    ]
  },
  {
    category: 'Tour Navigation (when a Guided Tour is active)',
    shortcuts: [
      { keys: '→', action: 'Next step' },
      { keys: '←', action: 'Previous step' },
      { keys: 'Esc', action: 'Skip / close tour' },
    ]
  }
];

export const GUIDED_TOURS = [
  {
    id: 'intro',
    title: 'Quick Start Tour',
    description: 'Learn the basics of the AV Simulation Tool interface',
    steps: [
      {
        target: '[data-testid="app-logo"]',
        title: 'AV Simulation Tool',
        content: 'Welcome! Your project name appears in the menu bar. The toolbar below is where most editing actions live.',
        position: 'bottom'
      },
      {
        target: '[data-testid="library-panel"]',
        title: 'Library Panel',
        content: 'Browse and add gear from here — Projectors, Truss & Scaffold, LED, Objects, Files, Content, and LED Test Patterns. The active tab glows fuchsia.',
        position: 'right'
      },
      {
        target: '[data-testid="move-tool-btn"]',
        title: 'Move Tool (W)',
        content: 'Click this or press W. Drag the colored gizmo arrows on a selected object to reposition it.',
        position: 'bottom'
      },
      {
        target: '[data-testid="rotate-tool-btn"]',
        title: 'Rotate Tool (E)',
        content: 'Rotate objects around any axis by dragging the colored rings.',
        position: 'bottom'
      },
      {
        target: '[data-testid="scale-tool-btn"]',
        title: 'Scale Tool (R)',
        content: 'Resize objects by dragging the scale handles.',
        position: 'bottom'
      },
      {
        target: '[data-testid="snap-tool-btn"]',
        title: 'Snap Tool',
        content: 'Toggle grid snapping for precise alignment of multiple projectors or truss sections.',
        position: 'bottom'
      },
      {
        target: '[data-testid="lux-heatmap-btn"]',
        title: 'Lux Heatmap',
        content: 'Visualize light intensity across all surfaces. Blue = low, Green = medium, Red = high lux.',
        position: 'bottom'
      },
      {
        target: '[data-testid="display-mode-btn"]',
        title: 'Display Mode (Outline / Shaded)',
        content: 'Switch between full-render Shaded mode and lightweight Outline (wireframe) mode. Outline is great for layout planning and gives a small perf boost.',
        position: 'bottom'
      },
      {
        target: '[data-testid="fps-meter"]',
        title: 'Real-time FPS Meter',
        content: 'Live frame rate. Green = smooth (60+), Yellow = medium, Red = heavy (<30). Use it to gauge scene cost as you build.',
        position: 'bottom'
      },
      {
        target: '[data-testid="toggle-grid-btn"]',
        title: 'Viewport Controls',
        content: 'Toggle the ground grid, take a clean screenshot of the viewport, or expand to fullscreen — all from this top-right cluster.',
        position: 'bottom'
      }
    ]
  },
  {
    id: 'add-projector',
    title: 'Add a Projector',
    description: 'Place a Barco / Panasonic projector and see it in the scene',
    steps: [
      {
        target: '[data-testid="projectors-tab"]',
        title: 'Open the Projectors Tab',
        content: 'Click the Projectors tab in the Library Panel. The fuchsia glow shows it\'s active.',
        position: 'right',
        fallbackText: 'Click "Projectors" in the Library panel tab grid (top-left of left panel).'
      },
      {
        target: '[data-testid^="projector-"]',
        title: 'Browse the Library',
        content: 'Each card shows lumens, throw ratio range, weight, and aspect ratio. Pick the model that matches your real-world gear.',
        position: 'right'
      },
      {
        target: '[data-testid^="add-projector-btn-"]',
        title: 'Add to Scene',
        content: 'Click "+ Add to Scene" on any projector card. The projector appears at the origin — drag it into position with the Move tool (W).',
        position: 'right',
        fallbackText: 'Use the "+ Add to Scene" button on any projector card.'
      },
      {
        target: '[data-testid="export-projection-report"]',
        title: 'Projection Report',
        content: 'Once placed, generate a PDF report covering all projector specs, lux analysis, surface coverage, and weight breakdowns. The report includes the AV Simulation Tool logo on the cover page.',
        position: 'bottom',
        fallbackText: 'Find "Export Projection Report" in the Projectors menu.'
      }
    ]
  },
  {
    id: 'add-led',
    title: 'Add an LED Panel',
    description: 'Build an LED wall — flat, curved, corner, or flexible',
    steps: [
      {
        target: '[data-testid="led-tab"]',
        title: 'Open the LED Tab',
        content: 'The LED tab is in the Library Panel. It uses a 2-column grid for quick browsing of all LED variants.',
        position: 'right'
      },
      {
        target: '[data-testid^="add-led_screen-"]',
        title: 'LED Screen (Flat)',
        content: 'Standard flat LED wall. Configure tile size, pixel pitch, columns, and rows in the Properties panel.',
        position: 'right',
        fallbackText: 'Pick LED Screen from the LED tab grid.'
      },
      {
        target: '[data-testid^="add-led_curved-"]',
        title: 'Curved LED',
        content: 'LED panels arranged in a curve. Set the curve angle in Properties to control the bend.',
        position: 'right',
        fallbackText: 'Pick LED Curved for curved panels.'
      },
      {
        target: '[data-testid^="add-led_corner-"]',
        title: 'Corner / Soft Corner / Flexible',
        content: 'Corner tiles for inside/outside angles. Soft Corner for chamfered transitions. Flexible for organic wraps.',
        position: 'right'
      },
      {
        target: '[data-testid="led-test-tab"]',
        title: 'LED Test Patterns',
        content: 'After placing your wall, switch to the LED Test Pattern tab to apply alignment / pixel-check patterns.',
        position: 'right'
      }
    ]
  },
  {
    id: 'add-truss',
    title: 'Add Truss',
    description: 'Build truss rigs from sections + corners',
    steps: [
      {
        target: '[data-testid="truss-tab"]',
        title: 'Truss & Scaffold Tab',
        content: 'Truss and scaffolding share one tab in the Library Panel. Truss sections come in 2 m / 3 m / 4 m lengths plus corner pieces.',
        position: 'right'
      },
      {
        target: '[data-testid^="add-truss-"][data-testid$="-2m-btn"]',
        title: 'Add a 2 m Section',
        content: 'Click any "Add 2m" / "Add 3m" / "Add 4m" button. The section appears at origin — use Move (W) and Rotate (E) to position.',
        position: 'right',
        fallbackText: 'Use the Add 2m / 3m / 4m buttons in the Truss & Scaffold tab.'
      },
      {
        target: '[data-testid^="add-truss-corner-"]',
        title: 'Corner Pieces',
        content: 'Connect sections at right angles with the 0.5 m corner piece. Snap (Magnet icon) helps align ends precisely.',
        position: 'right'
      }
    ]
  },
  {
    id: 'add-scaffolding',
    title: 'Add Scaffolding',
    description: 'Place a scaffolding tower with decks, stairs, and load tracking',
    steps: [
      {
        target: '[data-testid="truss-tab"]',
        title: 'Truss & Scaffold Tab',
        content: 'Scaffolding lives in the same tab as Truss in the Library Panel.',
        position: 'right'
      },
      {
        target: '[data-testid="add-scaffolding-btn"]',
        title: 'Add Scaffolding Tower',
        content: 'Click "Add Scaffolding" — a default tower drops into the scene. Open the Properties panel to configure bay size, lift height, levels, decks, and stairs.',
        position: 'right',
        fallbackText: 'Use the "Add Scaffolding" button in the Truss & Scaffold tab.'
      },
      {
        target: '[data-testid="properties-panel"]',
        title: 'Configure Decks & Stairs',
        content: 'Per-level deck slots and stair configuration are in the Properties panel. Load calculations update live as you add equipment to each deck.',
        position: 'left',
        fallbackText: 'Edit scaffolding details in the Properties panel on the right.'
      }
    ]
  },
  {
    id: 'add-object',
    title: 'Add an Object / Surface',
    description: 'Walls, planes, screens, and 3D primitives',
    steps: [
      {
        target: '[data-testid="primitives-tab"]',
        title: 'Objects Tab',
        content: 'The Objects tab in the Library Panel holds primitives: Cube, Sphere, Wall/Plane, Cylinder, Ground, Dome, Pipe, Scrim Screen, plus pre-sized Projection Screens (16:10 small / medium / large).',
        position: 'right'
      },
      {
        target: '[data-testid="properties-panel"]',
        title: 'Configure Dimensions',
        content: 'After dropping an object, the Properties panel shows precise Width / Height / Depth inputs in your selected unit (mm / cm / m / ft / in).',
        position: 'left',
        fallbackText: 'Edit object dimensions in the Properties panel on the right.'
      }
    ]
  },
  {
    id: 'display-mode-perf',
    title: 'Display Mode + FPS Workflow',
    description: 'How to use Outline mode and the FPS meter together for layout reviews',
    steps: [
      {
        target: '[data-testid="display-mode-btn"]',
        title: 'Toggle Outline Mode',
        content: 'Click here to switch the entire scene to wireframe rendering. All viewports (perspective + orthographic) update instantly.',
        position: 'bottom'
      },
      {
        target: '[data-testid="fps-meter"]',
        title: 'Watch the FPS',
        content: 'Note the frame-rate change. Heavy projection scenes typically jump 20–40% in Outline mode (fragment shading drops out).',
        position: 'bottom'
      },
      {
        target: '[data-testid="display-mode-btn"]',
        title: 'Return to Shaded',
        content: 'Click again to return to the full Shaded view for the final visual review or screenshot.',
        position: 'bottom'
      }
    ]
  },
  {
    id: 'screenshot-fullscreen',
    title: 'Screenshot & Fullscreen',
    description: 'Capture a clean PNG or expand to full screen',
    steps: [
      {
        target: '[data-testid="toggle-grid-btn"]',
        title: 'Toggle Grid',
        content: 'Toggle the ground grid for cleaner screenshots. Highlights fuchsia when on.',
        position: 'bottom'
      },
      {
        target: '[data-testid="screenshot-btn"]',
        title: 'Take Screenshot',
        content: 'Captures a clean PNG of the viewport (no UI overlays) and downloads it. Uses Babylon\'s native render-target — full resolution.',
        position: 'bottom'
      },
      {
        target: '[data-testid="fullscreen-btn"]',
        title: 'Fullscreen',
        content: 'Expand the viewport to full screen via the browser Fullscreen API. Press Esc or click again to exit.',
        position: 'bottom'
      }
    ]
  },
  {
    id: 'projector-pov',
    title: 'Projector POV Window',
    description: 'Open a floating preview of what the projector sees — multi-monitor ready',
    steps: [
      {
        target: '[data-testid^="scene-projector-"]',
        title: 'Select a Projector',
        content: 'Click any projector row in the Scene panel (right side, top).',
        position: 'left',
        fallbackText: 'Add a projector first if you don\'t have one in the scene.'
      },
      {
        target: '[data-testid^="scene-projector-"] button[title*="POV"]',
        title: 'Open POV Window',
        content: 'Click the camera icon on the projector row to spawn a floating preview that mirrors the projector\'s point of view.',
        position: 'left'
      },
      {
        target: '[data-testid="properties-panel"]',
        title: 'Pop Out to Second Monitor',
        content: 'POV windows have a "Pop out" button that opens a separate browser window — drag it to a second monitor for live "stage" preview during shows.',
        position: 'left'
      }
    ]
  }
];
